import { el } from "./dom";

export interface CycleBadgeOptions<T> {
  readonly label: string;
  readonly value: T;
  readonly options: readonly T[];
  readonly format: (value: T) => string;
  readonly onChange: (next: T) => void;
}

/**
 * Badge-styled cycler — label stacked above a `◀ value ▶` row. The arrows
 * step backward/forward through `options` (wrapping at both ends); clicking
 * the badge body cycles forward. The whole thing is keyboard-reachable and
 * exposes a readable aria-label.
 */
export function createCycleBadge<T>(opts: CycleBadgeOptions<T>): HTMLElement {
  const idx = opts.options.indexOf(opts.value);
  const safeIdx = idx >= 0 ? idx : 0;
  const len = opts.options.length;

  const step = (delta: 1 | -1): void => {
    if (len === 0) return;
    const next = opts.options[(safeIdx + delta + len) % len];
    if (next !== undefined) opts.onChange(next);
  };

  const stop = (handler: () => void) => (ev: Event): void => {
    ev.stopPropagation();
    handler();
  };

  const prev = el(
    "button",
    {
      class: "cycle-badge__arrow",
      attrs: {
        type: "button",
        "aria-label": `${opts.label}: previous`,
        "data-selector-arrow": "prev",
      },
      on: { click: stop(() => step(-1)) },
    },
    ["◀"],
  );

  const next = el(
    "button",
    {
      class: "cycle-badge__arrow",
      attrs: {
        type: "button",
        "aria-label": `${opts.label}: next`,
        "data-selector-arrow": "next",
      },
      on: { click: stop(() => step(1)) },
    },
    ["▶"],
  );

  const valueText = opts.format(opts.value);
  const valueNode = el("div", { class: "variant-badge__value", text: valueText });

  return el(
    "div",
    {
      class: "variant-badge variant-badge--interactive",
      attrs: {
        role: "button",
        tabindex: "0",
        "aria-label": `${opts.label}: ${valueText}`,
        // Outer focus key for cross-repaint focus survival. Label is unique
        // per badge within a screen; same-label collisions would be a screen
        // bug anyway (two badges labelled "Players" would confuse SR users).
        "data-focus-key": `cycle:${opts.label}`,
      },
      on: {
        click: () => step(1),
        keydown: (ev: Event) => {
          // Ignore keys bubbling up from the focused arrow buttons — they have
          // their own click handlers, and intercepting Enter/Space here would
          // suppress the button's synthetic click and cycle in the wrong
          // direction.
          if (ev.target !== ev.currentTarget) return;
          const ke = ev as KeyboardEvent;
          if (ke.key === "Enter" || ke.key === " ") {
            ev.preventDefault();
            step(1);
          } else if (ke.key === "ArrowLeft") {
            ev.preventDefault();
            step(-1);
          } else if (ke.key === "ArrowRight") {
            ev.preventDefault();
            step(1);
          }
        },
      },
    },
    [
      el("div", { class: "variant-badge__label", text: opts.label }),
      el("div", { class: "cycle-badge__row" }, [prev, valueNode, next]),
    ],
  );
}
